import { getCurrencyConversions } from '@/redux/modules/currency';
import { getItemSummaryCurrency } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveCatalogCurrentItemId } from '@/redux/modules/console';
import { getLocale, switchLocale } from '@/redux/modules/intl';
import { Select } from '@liveauctioneers/hammer-ui-core/select';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import UILanguages from '@/intl/UILanguages';
import useCurrencyConversion from './hooks/useCurrencyConversion';

type Props = {
    catalogId: number;
    className?: string;
    hideLabels?: boolean;
    onCurrencyChanged: (value) => void;
    selectedCurrency: string;
};

const Conversions = ({ catalogId, className, hideLabels, onCurrencyChanged, selectedCurrency }: Props) => {
    const { formatMessage, formatNumber } = useIntl();
    const dispatch = useAppDispatch();

    const conversions = useAppSelector(getCurrencyConversions);
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));

    const locale = useAppSelector(getLocale);

    const { targetCurrencyCode } = useCurrencyConversion({
        catalogId,
        selectedCurrency,
    });

    const symbolFromIntl = (currency) => {
        return formatNumber(0, {
            currency,
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
            style: 'currency',
        })
            .replace(/\s/g, '')
            .replace('0', '');
    };

    const mapConversions = (conversion) => {
        const currencySymbol = symbolFromIntl(currency);
        const symbol = symbolFromIntl(conversion.currencyCode);

        return {
            label: currencySymbol !== symbol ? `${currencySymbol} → ${symbol}` : currencySymbol,
            value: conversion.currencyCode,
        };
    };

    const onLanguageChange = ({ value }) => {
        // TODO fix screen reloading
        dispatch(switchLocale(value));
    };

    const handleCurrencyChanged = ({ value }) => {
        onCurrencyChanged(value);
    };

    const languageOptions = UILanguages.map((x) => ({
        label: x.name,
        value: x.code,
    }));
    const currencyOptions = conversions.map(mapConversions);

    return (
        <div className={className}>
            <Select
                className="mb-sm"
                labelText={
                    hideLabels
                        ? undefined
                        : formatMessage({
                              id: 'bidding_console.console.settingsMenu.language',
                          })
                }
                onChange={onLanguageChange}
                options={languageOptions}
                small
                usePortalMenu
                value={languageOptions.find((op) => op.value === locale)}
            />
            <Select
                labelText={
                    hideLabels
                        ? undefined
                        : formatMessage({
                              id: 'bidding_console.console.settingsMenu.currency',
                          })
                }
                onChange={handleCurrencyChanged}
                options={currencyOptions}
                small
                usePortalMenu
                value={currencyOptions.find((op) => op.value === targetCurrencyCode)}
            />
        </div>
    );
};

export default Conversions;
